import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { customFetcher } from '../useFetch';

export interface IFinTSInstitute {
  idFinTSInstitute: number;
  Blz: string;
  Bic?: string | null;
  Institut: string;
  Ort?: string | null;
  Rz?: string | null;
  Organisation?: string | null;
  HbciUrl?: string | null;
  Version?: string | null;
  SourceLastChanged?: string | null;
}

export interface IFinTSImportResult {
  parsed: number;
  mode: 'local' | 'migration' | 'both';
  inserted?: number;
  updated?: number;
  migrationFile?: string;
}

export interface IFinTSImportRequest {
  FileName: string;
  DataBase64: string;
  Mode: 'local' | 'migration' | 'both';
}

const QK_BLZ = (blz: string) => ['fintsInstitute', 'byBlz', blz];
const QK_SEARCH = (q: string) => ['fintsInstitute', 'search', q];
const QK_COUNT = ['fintsInstitute', 'count'];

/** Lookup pro BLZ — fuer Auto-Fill in der HBCI-Konto-Anlage. Liefert NULL,
 *  solange die BLZ keine 8 Ziffern hat oder unbekannt ist. */
export const useFinTSInstituteByBlz = (blz: string | null | undefined) => {
  const cleaned = (blz ?? '').trim();
  const enabled = /^\d{8}$/.test(cleaned);
  const { data, isFetching } = useQuery<IFinTSInstitute[]>({
    enabled,
    refetchOnWindowFocus: false,
    staleTime: 60 * 60 * 1000,
    queryKey: QK_BLZ(cleaned),
    queryFn: () =>
      customFetcher({
        endpoint: `/fintsinstitute/by-blz/${encodeURIComponent(cleaned)}`,
      }).catch(() => [] as IFinTSInstitute[]),
  });
  return {
    institutes: data ?? [],
    isFetchingInstitute: isFetching,
  };
};

/**
 * Freitext-Suche im FinTS-Katalog — sucht in BLZ, BIC, Institut und Ort.
 * Erst ab >=2 Zeichen aktiv. Liefert ein leeres Array zurueck, solange der
 * Suchbegriff zu kurz ist (kein Backend-Roundtrip).
 *
 * Cache 30 Sekunden — gleiches Tippen direkt hintereinander hits den Cache,
 * verhindert aber dass veraltete Treffer "ewig" haengen bleiben.
 */
export const useSearchFinTSInstitutes = (q: string | null | undefined) => {
  const term = (q ?? '').trim();
  const enabled = term.length >= 2;
  const { data, isFetching } = useQuery<IFinTSInstitute[]>({
    enabled,
    refetchOnWindowFocus: false,
    staleTime: 30 * 1000,
    queryKey: QK_SEARCH(term.toLowerCase()),
    queryFn: () =>
      customFetcher({
        endpoint: `/fintsinstitute/search?q=${encodeURIComponent(term)}`,
      })
        .then(r => (Array.isArray(r) ? r : []))
        .catch(() => [] as IFinTSInstitute[]),
  });
  return {
    institutes: Array.isArray(data) ? data : [],
    isSearchingInstitutes: isFetching,
  };
};

export const useFinTSInstituteCount = () => {
  const { data, refetch, isLoading } = useQuery<{
    Total: number;
    LastUpdated: string | null;
  }>({
    refetchOnWindowFocus: false,
    queryKey: QK_COUNT,
    queryFn: () => customFetcher({ endpoint: '/admin/fintsinstitute/count' }),
  });
  return {
    instituteCount: data?.Total ?? 0,
    lastUpdated: data?.LastUpdated ?? null,
    refetchCount: refetch,
    isLoadingCount: isLoading,
  };
};

export const useImportFinTSInstitutes = () => {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending } = useMutation<
    IFinTSImportResult,
    Error,
    IFinTSImportRequest
  >({
    mutationFn: body =>
      customFetcher({
        endpoint: '/admin/fintsinstitute/import',
        method: 'POST',
        body,
      }) as Promise<IFinTSImportResult>,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['fintsInstitute'] });
    },
  });
  return {
    importFinTSInstitutes: mutateAsync,
    isImportingFinTSInstitutes: isPending,
  };
};
